import {
  type AnimatedAssetMetadata,
  type AssetManifest,
  type AssetMetadata,
  assetManifestSchema,
  fishArt,
  itemArt,
  type StaticAssetMetadata,
} from '@kenocyber/shared';

/**
 * Runtime access to the generated asset manifest.
 *
 * The manifest is produced by `@kenocyber/asset-pipeline` and validated with
 * the exact shared schema on load - a stale or hand-edited manifest fails
 * loudly instead of rendering garbage. Only the assets required by the current
 * hunt are ever loaded into textures; the manifest itself is a small JSON.
 */

export const MANIFEST_URL = '/game-assets/manifest.json';

export async function fetchAssetManifest(): Promise<AssetManifest> {
  const response = await fetch(MANIFEST_URL);
  if (!response.ok) {
    throw new Error(`asset manifest not found (${response.status}). Run: pnpm assets:prepare`);
  }
  return assetManifestSchema.parse(await response.json());
}

export function findAsset(manifest: AssetManifest, id: string): AssetMetadata {
  const asset = manifest.assets.find((entry) => entry.id === id);
  if (!asset) {
    throw new Error(`asset '${id}' missing from manifest`);
  }
  return asset;
}

export function findAnimatedAsset(manifest: AssetManifest, id: string): AnimatedAssetMetadata {
  const asset = findAsset(manifest, id);
  if (asset.kind !== 'animated') {
    throw new Error(`asset '${id}' is not animated`);
  }
  return asset;
}

export function findStaticAsset(manifest: AssetManifest, id: string): StaticAssetMetadata {
  const asset = findAsset(manifest, id);
  if (asset.kind !== 'static') {
    throw new Error(`asset '${id}' is not static`);
  }
  return asset;
}

export function assetUrl(manifest: AssetManifest, asset: AssetMetadata): string {
  return `${manifest.baseUrl}${asset.file}`;
}

/**
 * URL of a static asset by id, or `null` when the manifest has none.
 *
 * Tolerant on purpose: the inventory lists whatever the vault owns, and a
 * definition whose art does not exist yet must render a placeholder frame
 * rather than throw and take the whole panel down with it. A missing PNG is
 * never invented - the cell simply shows it has none.
 */
export function staticAssetUrl(manifest: AssetManifest, id: string): string | null {
  const asset = manifest.assets.find((entry) => entry.id === id);
  return asset && asset.kind === 'static' ? assetUrl(manifest, asset) : null;
}

/**
 * **O URL da imagem de uma definição, seja qual for o manifesto que a tem.**
 *
 * O passo do cliente para o resolvedor canónico `itemArt`, e o único: ele diz
 * QUAL manifesto responde por aquela definição, e isto transforma a resposta num
 * endereço. Um caminho composto à mão — `jewels/${id}.png` — produziria um
 * pedido para um ficheiro que pode não estar lá, e é o que os quatro manifestos
 * existem para impedir.
 *
 * As duas metades, e são as que `ItemArt` já distingue:
 *
 * - `asset` — está no manifesto GERAL, e passa por `staticAssetUrl`, que é
 *   tolerante: uma chave que ele não descreve devolve `null` em vez de atirar;
 * - `file` — está num manifesto PRÓPRIO (cartas, joias, skins), que já traz o
 *   caminho servido, e só falta pôr-lhe o `baseUrl` à frente.
 *
 * `null` é uma resposta legítima e continua a sê-lo: hoje é o que as rações de
 * pet, as esferas de energia, os baldes de tinta e o Estabilizador Quântico
 * devolvem, porque os masters deles não existem. Quem desenha mostra o recuo que
 * já tem — nunca o id, e nunca a arte de outro item.
 */
export function itemArtUrl(
  manifest: AssetManifest,
  definitionId: string,
  level = 1,
): string | null {
  const art = itemArt(definitionId, level);
  if (art === null) {
    return null;
  }
  return art.kind === 'asset'
    ? staticAssetUrl(manifest, art.assetKey)
    : `${manifest.baseUrl}${art.file}`;
}

/**
 * **O URL da arte de uma espécie de peixe** (gate V28I.BK).
 *
 * O gémeo de {@link itemArtUrl}, com a mesma disciplina e uma porta separada,
 * porque **um peixe não é uma `ItemDefinition`**: não tem nível, não funde, não
 * equipa e não entra no inventário. Passá-lo por `itemArt` obrigaria o
 * resolvedor canónico de itens a conhecer um universo que não é o dele.
 *
 * Um único passo, como o outro: `fishArt` diz qual é o ficheiro servido, e isto
 * põe-lhe o `baseUrl` à frente. Compor `fish/${id}.png` à mão produziria um
 * pedido para um ficheiro que pode não estar lá.
 *
 * `null` é uma resposta legítima, e é a das vinte espécies RETIRADAS: elas
 * continuam a existir no registo de uma conta, valem o que valiam e chamam-se o
 * que se chamavam, mas nunca tiveram arte. Quem desenha mostra o recuo que já
 * tem — nunca o id, e nunca a arte de outra espécie.
 */
export function fishArtUrl(manifest: AssetManifest, speciesId: string): string | null {
  const art = fishArt(speciesId);
  return art === null ? null : `${manifest.baseUrl}${art.file}`;
}
